// What happened to a week, newest first (round 44). The worker reads the same
// list the office does: a correction to somebody's hours they cannot see is
// what record-keeping rules exist to stop.
import { minutesToHm } from '../../lib/timesheetHours';
import { formatMomentAu } from '../../lib/dates';
import { clock, dayLabel } from '../../lib/timesheetWeeks';
import type { TimesheetDetail, TimesheetEvent } from '../../timesheetsApi';

const FIELD: Record<string, string> = {
  startTime: 'Start',
  finishTime: 'Finish',
  breakMinutes: 'Break',
  job: 'Job',
  leaveTypeId: 'Leave',
  leaveMinutes: 'Leave hours',
  allowanceTypeIds: 'Allowances',
  location: 'Place',
  note: 'Note',
  weekNote: 'Note for the week',
};

type Change = NonNullable<TimesheetEvent['detail']['changes']>[number];

/** One change as a sentence: "Tue 6 Oct: Start 7:00am to 6:30am". */
export function changeText(change: Change, names: Map<string, string>): string {
  const label = FIELD[change.field] ?? change.field;
  const prefix = change.field === 'weekNote' ? '' : `${dayLabel(change.date)}: `;
  if (change.changed) return `${prefix}${label} changed`;
  const show = (value: unknown): string => {
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return 'none';
    switch (change.field) {
      case 'startTime':
      case 'finishTime':
        return clock(String(value));
      case 'breakMinutes':
      case 'leaveMinutes':
        return minutesToHm(Number(value));
      case 'leaveTypeId':
        return names.get(String(value)) ?? 'a removed kind of leave';
      case 'allowanceTypeIds':
        return (value as string[]).map((id) => names.get(id) ?? 'removed').join(', ');
      default:
        return String(value);
    }
  };
  return `${prefix}${label} ${show(change.before)} to ${show(change.after)}`;
}

export function eventHeadline(event: TimesheetEvent): string {
  switch (event.kind) {
    case 'created':
      return event.detail.onBehalf ? `${event.actorName} started this week for them` : `${event.actorName} started the week`;
    case 'edited':
      return `${event.actorName} made changes`;
    case 'submitted':
      return event.detail.onBehalf
        ? `${event.actorName} entered and submitted it for the worker (not signed)`
        : `${event.actorName} signed and submitted it`;
    case 'approved':
      return `${event.actorName} approved it`;
    case 'returned':
      return `${event.actorName} sent it back`;
    case 'file_added':
      return `${event.actorName} added ${event.detail.filename ?? 'a document'}`;
    case 'file_removed':
      return `${event.actorName} removed ${event.detail.filename ?? 'a document'}`;
  }
}

export function TimesheetHistory({ detail }: { detail: TimesheetDetail }) {
  const names = new Map([...detail.leaveTypes, ...detail.allowanceTypes].map((t) => [t.id, t.name]));
  if (detail.events.length === 0) {
    return (
      <section className="panel">
        <p className="muted !mb-0">Nothing has happened to this week yet.</p>
      </section>
    );
  }
  return (
    <section className="panel">
      <h2>History</h2>
      <ol className="space-y-3">
        {detail.events.map((event) => (
          <li key={event.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
            <p className="!mb-0 text-sm font-medium text-gray-900">{eventHeadline(event)}</p>
            <p className="!mb-0 text-xs text-gray-500">{formatMomentAu(event.at)}</p>
            {event.reason && <p className="!mb-0 mt-1 text-sm text-gray-700">“{event.reason}”</p>}
            {event.kind === 'edited' && event.detail.changes && (
              <ul className="mt-1 list-disc pl-5 text-sm text-gray-700">
                {event.detail.changes.map((change, i) => (
                  <li key={i}>{changeText(change, names)}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
